import { useState, useEffect, useCallback } from 'react';
import { Control, UseFormRegister, FieldErrors } from 'react-hook-form';

import { useAtom } from 'jotai';

import { getGuardians } from '@/api/getGuardians';
import { getOffices } from '@/api/getOffices';
import DateField from '@/components/DateField/DateField';
import InputText from '@/components/InputText/InputText';
import MultiSelectField from '@/components/SelectField/MultiSelectField';
import SelectField from '@/components/SelectField/SelectField';
import { PREFECTURE_OPTIONS } from '@/const/prefecture';
import { UserAtom } from '@/jotai/jotai';
import { guardianRelationshipOptions } from '@/logics/guardian/guardian';
import { UpdateChildFormData } from '@/pages/admin/children/[id]';
import { Child } from '@/types/child';
import { Guardian } from '@/types/guardian';
import { Office } from '@/types/office';

type Props = {
  child: Child | undefined;
  errors: FieldErrors<UpdateChildFormData>;
  control: Control<UpdateChildFormData>;
  register: UseFormRegister<UpdateChildFormData>;
};

export const BasicInfoForm = ({ child, errors, control, register }: Props) => {
  const [guardians, setGuardians] = useState<Guardian[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);

  const [loginUser] = useAtom(UserAtom);

  useEffect(() => {
    const fetchGuardians = async () => {
      const result = await getGuardians(loginUser?.email);
      if (result.status === 200) {
        setGuardians(result.result);
        console.log(result.result);
      }
    };
    fetchGuardians();
    fetchOffices();
  }, [loginUser?.email]);

  const fetchOffices = useCallback(async () => {
    try {
      const result = await getOffices(loginUser?.email || '');
      if (result.status === 200) {
        setOffices(result.result);
      }
    } catch (error) {
      console.log('error');
    }
  }, [loginUser?.email]);

  return (
    <div className="flex flex-col justify-center">
      <div className="flex flex-col">
        <div className="flex flex-col gap-4">
          <span className="text-lg font-bold">管理番号</span>
          <span>{child?.id}</span>
        </div>
        <div className="w-1/2">
          <DateField
            label="利用契約開始日"
            required={true}
            errors={errors.contractStartDate}
            control={control}
            name="contractStartDate"
          />
        </div>
        <div className="w-1/2"></div>
        <div className="w-1/2">
          <DateField
            label="利用契約終了日"
            required={false}
            control={control}
            name="contractEndDate"
          />
        </div>
        <div className="w-1/2"></div>
      </div>
      <div className="flex flex-row gap-4">
        <InputText
          label="利用者（児童）（姓）"
          type="text"
          required={true}
          errors={errors.lastName}
          {...register('lastName', { required: true })}
        />
        <InputText
          label="利用者（児童）（名）"
          type="text"
          required={true}
          errors={errors.firstName}
          {...register('firstName', { required: true })}
        />
      </div>
      <div className="flex flex-row gap-4">
        <InputText
          label="利用者（児童）（セイ）"
          type="text"
          required={true}
          errors={errors.lastNameKana}
          {...register('lastNameKana', { required: true })}
        />
        <InputText
          label="利用者（児童）（メイ）"
          type="text"
          required={true}
          errors={errors.firstNameKana}
          {...register('firstNameKana', { required: true })}
        />
      </div>
      <div className="w-1/2">
        <DateField
          label="生年月日"
          required={true}
          errors={errors.birthDate}
          control={control}
          name="birthDate"
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="guardianId1"
          label="保護者_1"
          control={control}
          items={guardians.map((guardian) => ({
            label: guardian.last_name + guardian.first_name,
            value: guardian.id.toString(),
          }))}
          required={true}
        />
        <SelectField
          name="guardianRelationship1"
          label="保護者_1（関係性）"
          control={control}
          items={guardianRelationshipOptions}
          required={true}
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="guardianId2"
          label="保護者_2"
          control={control}
          items={guardians.map((guardian) => ({
            label: guardian.last_name + guardian.first_name,
            value: guardian.id.toString(),
          }))}
          required={false}
        />
        <SelectField
          name="guardianRelationship2"
          label="保護者_2（関係性）"
          control={control}
          items={guardianRelationshipOptions}
          required={false}
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="guardianId3"
          label="保護者_3"
          control={control}
          items={guardians.map((guardian) => ({
            label: guardian.last_name + guardian.first_name,
            value: guardian.id.toString(),
          }))}
          required={false}
        />
        <SelectField
          name="guardianRelationship3"
          label="保護者_3（関係性）"
          control={control}
          items={guardianRelationshipOptions}
          required={false}
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="guardianId4"
          label="保護者_4"
          control={control}
          items={guardians.map((guardian) => ({
            label: guardian.last_name + guardian.first_name,
            value: guardian.id.toString(),
          }))}
          required={false}
        />
        <SelectField
          name="guardianRelationship4"
          label="保護者_4（関係性）"
          control={control}
          items={guardianRelationshipOptions}
          required={false}
        />
      </div>
      <div className="flex flex-row gap-4">
        <InputText
          label="メールアドレス"
          type="text"
          required={true}
          errors={errors.email}
          {...register('email', { required: true })}
        />
        <InputText
          label="連絡先TEL"
          type="text"
          required={true}
          errors={errors.phoneNumber}
          {...register('phoneNumber', { required: true })}
        />
      </div>
      <div className="w-1/2">
        <InputText
          label="郵便番号"
          type="text"
          required={true}
          errors={errors.postalCode}
          {...register('postalCode', { required: true })}
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="prefecture"
          label="都道府県"
          control={control}
          items={PREFECTURE_OPTIONS.map((prefecture) => ({
            label: prefecture.name,
            value: prefecture.name,
          }))}
          required={true}
        />
        <InputText
          label="市区町村"
          type="text"
          required={true}
          errors={errors.city}
          {...register('city', { required: true })}
        />
      </div>
      <div className="flex flex-row gap-4">
        <InputText
          label="住所1"
          type="text"
          required={true}
          errors={errors.address1}
          {...register('address1', { required: true })}
        />
        <InputText
          label="住所2"
          type="text"
          required={false}
          {...register('address2')}
        />
      </div>
      <div className="flex flex-row gap-4">
        <SelectField
          name="emergencyContactRelation"
          label="緊急連絡先（関係性）"
          control={control}
          items={guardianRelationshipOptions}
          required={true}
        />
        <InputText
          label="緊急連絡先（TEL）"
          type="text"
          required={true}
          errors={errors.emergencyContactPhone}
          {...register('emergencyContactPhone', { required: true })}
        />
      </div>
      <div className="flex justify-between space-x-2 p-4">
        <MultiSelectField
          name="office_ids"
          label="事業所"
          placeholder="事業所を選択"
          options={offices
            .filter((office) => loginUser && loginUser.office_ids.includes(office.id))
            .map((office) => ({
              label: office.name,
              value: office.id,
            }))}
          defaultValue={offices
            .filter((office) => child && child.office_ids.includes(office.id))
            .map((office) => ({
              label: office.name,
              value: office.id,
            }))}
          control={control}
          required
        />
      </div>
    </div>
  );
};
